import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Sparkles, User, ChevronRight, FileText, Ticket } from 'lucide-react'
import { ToolUseCard } from './ToolUseCard'
import { formatCost, formatTokens } from './format'
import { bareName } from './toolDescriptors'
import { PROPOSE_TICKET_TOOL, openTicketReport, proposalOf } from './ticketProposal'
import { AttachmentViewer } from './AttachmentViewer'
import { openableAttachments } from './attachments'
import { notify } from '../../../notices'

function ThinkingSegment({ text }) {
  const [open, setOpen] = useState(false)
  if (!text) return null
  return (
    <div className="text-[10px] text-gray-400 italic my-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 hover:text-gray-600"
      >
        <ChevronRight size={10} className={'transition-transform ' + (open ? 'rotate-90' : '')} />
        Réflexion
      </button>
      {open && (
        <div className="ml-3 mt-1 pl-2 border-l border-gray-200 whitespace-pre-wrap">
          {text}
        </div>
      )}
    </div>
  )
}

/**
 * The agent's offer to open a ticket, rendered as a button.
 *
 * Always at the FOOT of the turn, whatever position the tool call had in the
 * stream: where the offer belongs on screen is a matter of reading order, not
 * something to leave to the order the model happened to emit.
 *
 * Nothing is created from here — the button opens the ordinary report form,
 * prefilled, and the editor still chooses the project and confirms.
 */
function TicketProposal({ proposal, messages }) {
  const [sent, setSent] = useState(false)

  const open = () => {
    if (openTicketReport({ proposal, messages })) {
      // The report window/popin takes over from here; the button stays visible
      // but spent, so a second click cannot open a second form.
      setSent(true)
      return
    }
    notify({
      type: 'error',
      message: "Le widget de signalement n'est pas chargé sur cette page : le ticket ne peut pas être ouvert d'ici.",
    })
  }

  return (
    <div className="mt-2 rounded-lg border border-teal-200 bg-teal-50/60 p-2.5">
      <div className="flex items-start gap-2">
        <Ticket size={14} className="text-teal-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium text-gray-800 break-words">{proposal.title}</div>
          {proposal.reason && (
            <div className="text-[11px] text-gray-500 mt-0.5 break-words">{proposal.reason}</div>
          )}
          <button type="button" className="btn btn-primary btn-sm mt-2" onClick={open} disabled={sent}>
            {sent ? 'Formulaire ouvert' : 'Créer un ticket'}
          </button>
        </div>
      </div>
    </div>
  )
}

function AssistantMessage({ msg, messages }) {
  const proposal = proposalOf(msg)
  return (
    <div className="flex gap-2 items-start">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center mt-0.5">
        <Sparkles size={14} />
      </div>
      <div className="flex-1 min-w-0">
        {msg.segments.map((seg, i) => {
          if (seg.type === 'thinking') return <ThinkingSegment key={i} text={seg.text} />
          // The proposal has its own rendering below; a tool card for it would
          // say the same thing twice, in the wrong place.
          if (seg.type === 'tool_use' && bareName(seg.name) === PROPOSE_TICKET_TOOL) return null
          if (seg.type === 'tool_use') return <ToolUseCard key={seg.id ?? i} tool={seg} />
          // text
          if (!seg.text) return null
          return (
            <div key={i} className="prose prose-sm max-w-none text-sm leading-snug break-words">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{seg.text}</ReactMarkdown>
            </div>
          )
        })}
        {proposal && <TicketProposal proposal={proposal} messages={messages} />}
        {msg.isError && msg.errorMessage && (
          <div className="text-[11px] text-red-600 mt-1 font-mono whitespace-pre-wrap">
            {msg.errorMessage}
          </div>
        )}
        {(msg.usage || msg.cost != null) && (
          <div className="text-[10px] text-gray-400 mt-2 flex gap-2">
            {formatCost(msg.cost) && <span>{formatCost(msg.cost)}</span>}
            {formatTokens(msg.usage) && <span className="font-mono">{formatTokens(msg.usage)}</span>}
          </div>
        )}
      </div>
    </div>
  )
}

function UserMessage({ msg }) {
  const attachments = msg.attachments ?? []
  // Une image envoyée ne doit pas rester une vignette de 64 px : c'est
  // justement la maquette dont on discute avec l'assistant.
  const openable = openableAttachments(attachments)
  const [openId, setOpenId] = useState(null)
  return (
    <div className="flex gap-2 items-start justify-end">
      <div className="flex-1 max-w-[85%] flex flex-col items-end gap-1.5">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 justify-end">
            {attachments.map(att => (
              att.kind === 'image' && att.thumbUrl ? (
                openable.some(a => a.id === att.id) ? (
                  <button
                    key={att.id}
                    type="button"
                    onClick={() => setOpenId(att.id)}
                    aria-label={`Ouvrir la pièce jointe ${att.name}`}
                    title={`Ouvrir ${att.name}`}
                    className="rounded-lg border border-gray-200 overflow-hidden hover:border-teal-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                  >
                    <img src={att.thumbUrl} alt={att.name} className="w-16 h-16 object-cover" />
                  </button>
                ) : (
                  <img
                    key={att.id}
                    src={att.thumbUrl}
                    alt={att.name}
                    title={att.name}
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                )
              ) : (
                <div
                  key={att.id}
                  title={att.name}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 text-gray-600 text-[11px] max-w-[150px]"
                >
                  <FileText size={12} className="flex-shrink-0" />
                  <span className="truncate">{att.name}</span>
                </div>
              )
            ))}
          </div>
        )}
        {msg.text && (
          <div className="bg-teal-600 text-white rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words">
            {msg.text}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center mt-0.5">
        <User size={14} />
      </div>
      <AttachmentViewer items={openable} openId={openId} onOpen={setOpenId} onClose={() => setOpenId(null)} />
    </div>
  )
}

export function MessageList({ messages, isStreaming }) {
  const ref = useRef(null)

  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight
  }, [messages, isStreaming])

  return (
    <div ref={ref} className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
      {messages.length === 0 && (
        <div className="text-center text-xs text-gray-400 py-8 px-4">
          <div className="mb-2">Demande-moi de gérer le catalogue de blocs.</div>
          <div className="text-[10px] text-gray-300">
            "Liste les blocs", "Crée un bloc citation", "Inspire-toi du HeroBlock pour…"
          </div>
        </div>
      )}
      {messages.map(msg => (
        msg.role === 'user'
          ? <UserMessage key={msg.id} msg={msg} />
          : <AssistantMessage key={msg.id} msg={msg} messages={messages} />
      ))}
      {isStreaming && messages.at(-1)?.role === 'user' && (
        <div className="flex gap-2 items-start">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center mt-0.5">
            <Sparkles size={14} />
          </div>
          <div className="flex-1 flex gap-1 items-center pt-2">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" />
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '150ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      )}
    </div>
  )
}
